/**
 * RF-02.3 - Status da OSC
 * Sistema de controle de status automático baseado no estado do cadastro e documentos
 */

export enum StatusOSC {
  EM_CADASTRO = 'EM_CADASTRO',
  DOCUMENTOS_INCOMPLETOS = 'DOCUMENTOS_INCOMPLETOS',
  AGUARDANDO_ANALISE = 'AGUARDANDO_ANALISE',
  EM_ANALISE = 'EM_ANALISE',
  APROVADO = 'APROVADO',
  REPROVADO = 'REPROVADO',
  SUSPENSA = 'SUSPENSA',
  INATIVA = 'INATIVA',
}

export const STATUS_OSC_LABELS: Record<StatusOSC, string> = {
  [StatusOSC.EM_CADASTRO]: 'Em Cadastro',
  [StatusOSC.DOCUMENTOS_INCOMPLETOS]: 'Documentos Incompletos',
  [StatusOSC.AGUARDANDO_ANALISE]: 'Aguardando Análise',
  [StatusOSC.EM_ANALISE]: 'Em Análise',
  [StatusOSC.APROVADO]: 'Aprovado',
  [StatusOSC.REPROVADO]: 'Reprovado',
  [StatusOSC.SUSPENSA]: 'Suspensa',
  [StatusOSC.INATIVA]: 'Inativa',
};

export const STATUS_OSC_COLORS: Record<StatusOSC, string> = {
  [StatusOSC.EM_CADASTRO]: 'bg-surface-muted text-fg border-line-strong',
  [StatusOSC.DOCUMENTOS_INCOMPLETOS]: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300',
  [StatusOSC.AGUARDANDO_ANALISE]: 'bg-warning-soft text-warning-text border-warning-line',
  [StatusOSC.EM_ANALISE]: 'bg-info-soft text-info-fg border-info-line',
  [StatusOSC.APROVADO]: 'bg-success-soft text-success-text border-success-line',
  [StatusOSC.REPROVADO]: 'bg-danger-soft text-danger-text border-danger-line',
  [StatusOSC.SUSPENSA]: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300',
  [StatusOSC.INATIVA]: 'bg-surface-muted text-fg-muted border-line-strong',
};

export const STATUS_OSC_DESCRIPTIONS: Record<StatusOSC, string> = {
  [StatusOSC.EM_CADASTRO]: 'Status inicial quando o cadastro é criado, antes da inserção de documentos',
  [StatusOSC.DOCUMENTOS_INCOMPLETOS]: 'Status automático para faltas obrigatórias ou documentos reprovados',
  [StatusOSC.AGUARDANDO_ANALISE]: 'Todos os documentos obrigatórios foram enviados; aguardando ser puxado para análise',
  [StatusOSC.EM_ANALISE]: 'Em análise pelo analista; pode ser aprovado ou reprovado',
  [StatusOSC.APROVADO]: 'Cadastro validado e aprovado pelo Analista ou Administrador',
  [StatusOSC.REPROVADO]: 'Cadastro rejeitado. Retorna para o Operador corrigir',
  [StatusOSC.SUSPENSA]: 'Cadastro temporariamente suspenso (com justificativa obrigatória)',
  [StatusOSC.INATIVA]: 'OSC desativada permanentemente',
};

export const STATUS_TRANSITIONS: Record<StatusOSC, StatusOSC[]> = {
  [StatusOSC.EM_CADASTRO]: [
    StatusOSC.DOCUMENTOS_INCOMPLETOS,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.DOCUMENTOS_INCOMPLETOS]: [
    StatusOSC.AGUARDANDO_ANALISE,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.AGUARDANDO_ANALISE]: [
    StatusOSC.EM_ANALISE,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.EM_ANALISE]: [
    StatusOSC.APROVADO,
    StatusOSC.REPROVADO,
    StatusOSC.SUSPENSA,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.APROVADO]: [
    StatusOSC.SUSPENSA,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.REPROVADO]: [
    StatusOSC.DOCUMENTOS_INCOMPLETOS,
    StatusOSC.AGUARDANDO_ANALISE,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.SUSPENSA]: [
    StatusOSC.AGUARDANDO_ANALISE,
    StatusOSC.EM_ANALISE,
    StatusOSC.APROVADO,
    StatusOSC.INATIVA,
  ],
  [StatusOSC.INATIVA]: [],
};

export const isValidTransition = (from: StatusOSC, to: StatusOSC): boolean =>
  STATUS_TRANSITIONS[from]?.includes(to) || false;

export interface StatusCalculationInput {
  temDocumentos: boolean;
  todosObrigatoriosEnviados: boolean;
  todosObrigatoriosAprovados?: boolean;
  statusAtual: StatusOSC;
}

export const calcularStatusAutomatico = (input: StatusCalculationInput): StatusOSC => {
  const { temDocumentos, todosObrigatoriosEnviados, todosObrigatoriosAprovados = false, statusAtual } = input;

  if (statusAtual === StatusOSC.SUSPENSA || statusAtual === StatusOSC.INATIVA
      || statusAtual === StatusOSC.EM_ANALISE) {
    return statusAtual;
  }

  if (!temDocumentos) {
    return StatusOSC.EM_CADASTRO;
  }

  if (!todosObrigatoriosEnviados) {
    return StatusOSC.DOCUMENTOS_INCOMPLETOS;
  }

  if (todosObrigatoriosAprovados) {
    return StatusOSC.APROVADO;
  }

  return StatusOSC.AGUARDANDO_ANALISE;
};

export interface StatusHistoryEntry {
  id?: string;
  statusAnterior: StatusOSC;
  statusNovo: StatusOSC;
  dataAlteracao: string;
  usuarioResponsavel: string;
  usuarioResponsavelNome?: string;
  justificativa?: string;
  observacoes?: string;
}

export interface AlterarStatusDTO {
  novoStatus: StatusOSC;
  justificativa: string;
  observacoes?: string;
}
